<script setup lang="ts">
import { computed, onMounted, type Ref, ref, toRefs } from 'vue';
import {
  LControl,
  LControlLayers,
  LIcon,
  LMap,
  LMarker,
  LPopup,
  LTileLayer,
  LTooltip,
} from "@vue-leaflet/vue-leaflet";
import PositionMarker from '@/components/marker/PositionMarker.vue';
import MarkerIcon from '@/components/marker/MarkerIcon.vue';
import RecapCard from '@/components/MaintenanceMap/RecapCard.vue';
import { useMapStore, maintenance_marker_color, get_preemptive_color } from '@/store/MapStore.ts';
import AlertIcon from '@/components/marker/AlertIcon.vue';
import {
  MaintenanceKind,
  MaintenanceMarker,
} from '@/components/MaintenanceMap/types';
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
const issuesStore = useMapStore();
const {
  show_preemptive, show_issues, agency_list, filtered_agencies, filtered_preemptives, filtered_parks,
  filtered_prio, sorted_preemptive, sorted_issues, all_markers
} = toRefs(issuesStore);

const center: Ref<[number, number]> = ref([48.005545, 3.012765]);
const zoom: Ref<number> = ref(7);

function center_on(marker: MaintenanceMarker) {
  center.value = [marker.location.lat, marker.location.lng];
}

const showing = ref(true);

const red_parks = computed(() => {
  return sorted_preemptive.value.filter(m => maintenance_marker_color(m) === get_preemptive_color("Red")).length;
});

const orange_parks = computed(() => {
  return sorted_preemptive.value.filter(m => maintenance_marker_color(m) === get_preemptive_color("Orange")).length;
});

const green_parks = computed(() => {
  return sorted_preemptive.value.filter(m => maintenance_marker_color(m) === get_preemptive_color("Green")).length;
});

const blue_parks = computed(() => {
  return sorted_preemptive.value.filter(m => maintenance_marker_color(m) === get_preemptive_color("Blue")).length;
});

onMounted(() => {
  issuesStore.load_data();
});

</script>

<i18n>
{
  "en": {
    "preemptive": "Preemptive",
    "issues": "Issues",
    "agencies": "Select Agencies:",
    "select_preemptive": "Select preemptive colors:",
    "issues_prio": "Select issues priority:",
    "park_name": "Park Name",
    "next_maint": "Next Maintenance",
    "issue_date": "Issue start date",
    "legend": "Legend",
    "red_text": "More than 6 months late",
    "orange_text": "Between 2 and 6 months late",
    "green_text": "Between 2 months late and 4 months in the future",
    "blue_text": "More than 4 months in the future",
    "parks": "Select Parks:"
  },
  "it": {
    "preemptive": "Preventive",
    "issues": "Problemi",
    "agencies": "Seleziona Agenzie:",
    "select_preemptive": "Seleziona colori preventivi:",
    "issues_prio": "Seleziona priorità dei problemi:",
    "park_name": "Nome del Parco",
    "next_maint": "Prossima Manutenzione",
    "issue_date": "Data di inizio del problema",
    "legend": "Legenda",
    "red_text": "Oltre 6 mesi di ritardo",
    "orange_text": "Tra 2 e 6 mesi di ritardo",
    "green_text": "Tra 2 mesi di ritardo e 4 mesi nel futuro",
    "blue_text": "Oltre 4 mesi nel futuro",
    "parks": "Seleziona i parchi:"
  },
  "fr": {
    "preemptive": "Préventif",
    "issues": "Problèmes",
    "agencies": "Sélectionner les Agences:",
    "select_preemptive": "Sélectionner les couleurs préventives:",
    "issues_prio": "Sélectionner la priorité des problèmes:",
    "park_name": "Nom du Parc",
    "next_maint": "Prochaine Maintenance",
    "issue_date": "Date de début du problème",
    "legend": "Légende",
    "red_text": "Plus de 6 mois de retard",
    "orange_text": "Entre 2 et 6 mois de retard",
    "green_text": "Entre 2 mois de retard et 4 mois dans le futur",
    "blue_text": "Plus de 4 mois dans le futur",
    "parks": "Sélectionner les parcs:"
  },
  "nl": {
    "preemptive": "Preventief",
    "issues": "Problemen",
    "agencies": "Selecteer Agentschappen:",
    "select_preemptive": "Selecteer preventieve kleuren:",
    "issues_prio": "Selecteer prioriteit van problemen:",
    "park_name": "Parknaam",
    "next_maint": "Volgende Onderhoud",
    "issue_date": "Startdatum probleem",
    "legend": "Legenda",
    "red_text": "Meer dan 6 maanden vertraging",
    "orange_text": "Tussen 0 en 6 maanden vertraging",
    "green_text": "Tussen 2 maanden te laat en 4 maanden in de toekomst",
    "blue_text": "Meer dan 4 maanden in de toekomst"
  }
}
</i18n>

<template>
  <v-container
    fluid
    style="height: 100%; width: 100%; position: relative;"
    class="pa-0 ma-0"
  >
    <v-navigation-drawer v-model="showing" width="400">
      <v-container>
        <v-row>
          <v-col cols="6">
            <v-checkbox :label="t('preemptive')" v-model="show_preemptive"/>
          </v-col>
          <v-col cols="6">
            <v-checkbox :label="t('issues')" v-model="show_issues"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="filtered_parks"
              :items="issuesStore.maintenance_park_names"
              item-value="id"
              item-title="name"
              :label="t('parks')"
              variant="solo"
              multiple
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="filtered_agencies"
              :items="agency_list"
              item-value="id"
              item-title="name"
              :label="t('agencies')"
              variant="solo"
              multiple
            />
          </v-col>
        </v-row>
        <v-row v-if="show_preemptive">
          <v-col>
            <v-autocomplete
              v-model="filtered_preemptives"
              :items="issuesStore.preemptive_colors"
              item-value="color"
              item-title="text"
              :label="t('select_preemptive')"
              variant="solo"
              multiple
            />
          </v-col>
        </v-row>
        <v-row v-if="show_issues">
          <v-col>
            <v-autocomplete
              v-model="filtered_prio"
              :items="issuesStore.priorities"
              item-value="value"
              item-title="text"
              :label="t('issues_prio')"
              variant="solo"
              multiple
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h3>{{ t('preemptive') }}</h3>
          </v-col>
        </v-row>
        <v-row v-if="show_preemptive">
          <v-col>
            <v-card class="outline">
              <v-card-title class="ma-1 pa-1">
                <h4>{{ t("legend") }}</h4>
              </v-card-title>
              <v-card-text class="ma-1">
                <v-row class="ma-1" v-if="filtered_preemptives.includes(get_preemptive_color('Red'))">
                  <v-col cols="2">
                    <v-icon>
                      <marker-icon
                        :colors="[{color: get_preemptive_color('Red')}]" :width="20" :height="20"
                      />
                    </v-icon>
                  </v-col>
                  <v-col cols="2">
                    {{ red_parks }}
                  </v-col>
                  <v-col>
                    {{ t('red_text') }}
                  </v-col>
                </v-row>
                <v-row class="ma-1" v-if="filtered_preemptives.includes(get_preemptive_color('Orange'))">
                  <v-col cols="2">
                    <v-icon>
                      <marker-icon
                        :colors="[{color: get_preemptive_color('Orange')}]" :width="20" :height="20"
                      />
                    </v-icon>
                  </v-col>
                  <v-col cols="2">
                    {{ orange_parks }}
                  </v-col>
                  <v-col>
                    {{ t('orange_text') }}
                  </v-col>
                </v-row>
                <v-row class="ma-1" v-if="filtered_preemptives.includes(get_preemptive_color('Green'))">
                  <v-col cols="2">
                    <v-icon>
                      <marker-icon
                        :colors="[{color: get_preemptive_color('Green')}]" :width="20" :height="20"
                      />
                    </v-icon>
                  </v-col>
                  <v-col cols="2">
                    {{ green_parks }}
                  </v-col>
                  <v-col>
                    {{ t('green_text') }}
                  </v-col>
                </v-row>
                <v-row class="ma-1" v-if="filtered_preemptives.includes(get_preemptive_color('Blue'))">
                  <v-col cols="2">
                    <v-icon>
                      <marker-icon
                        :colors="[{color: get_preemptive_color('Blue')}]" :width="20" :height="20"
                      />
                    </v-icon>
                  </v-col>
                  <v-col cols="2">
                    {{ blue_parks }}
                  </v-col>
                  <v-col>
                    {{ t('blue_text') }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="sorted_preemptive && sorted_preemptive.length != 0 && !issuesStore.loading_data">
          <v-col>
            {{ t('park_name') }}
          </v-col>
          <v-col style="text-align: center">
            {{ t('next_maint') }}
          </v-col>
        </v-row>
        <v-row v-if="issuesStore.loading_data">
          <v-col>
            <v-progress-linear indeterminate color="primary"/>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col v-if="sorted_preemptive && sorted_preemptive.length != 0">
            <v-list lines="one">
              <v-list-item
                v-for="marker in sorted_preemptive"
                :key="`map-${marker.name}-${marker.kind}`"
                density="compact"
                @click="center_on(marker)"
              >
                <template #prepend>
                  <v-icon>
                    <marker-icon
                      :colors="[{color: maintenance_marker_color(marker)}]" :width="20" :height="20"
                    />
                  </v-icon>
                </template>
                {{ marker.name }}
                <template #append> {{ new Date(marker.date).toLocaleDateString('it') }}</template>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col v-else>
            <p class="text-center text-grey">(no items to show)</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h3>{{ t('issues') }}</h3>
          </v-col>
        </v-row>
        <v-row v-if="sorted_issues && sorted_issues.length != 0 && !issuesStore.loading_data">
          <v-col>
            {{ t('park_name') }}
          </v-col>
          <v-col style="text-align: center">
            {{ t('issue_date') }}
          </v-col>
        </v-row>
        <v-row v-if="issuesStore.loading_data">
          <v-col>
            <v-progress-linear indeterminate color="primary"/>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col v-if="sorted_issues && sorted_issues.length != 0">
            <v-list lines="one">
              <v-list-item
                v-for="marker in sorted_issues"
                :key="`map-${marker.name}-${marker.kind}`"
                density="compact"
                @click="center_on(marker)"
              >
                <template #prepend>
                  <v-icon>
                    <alert-icon
                      :colors="[{color: maintenance_marker_color(marker)}]" :width="20" :height="20"
                    />
                  </v-icon>
                </template>
                {{ marker.name }}
                <template #append> {{ new Date(marker.date).toLocaleDateString('it') }}</template>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col v-else>
            <p class="text-center text-grey">(no items to show)</p>
          </v-col>
        </v-row>
      </v-container>
    </v-navigation-drawer>
    <v-row no-gutters style="height: 100%; width: 100%; position: relative;">
      <v-col>
        <l-map
          ref="map"
          :center="center"
          :zoom="zoom"
          :use-global-leaflet="false"
        >
          <l-control-layers position="topright"/>
          <l-control position="topleft">
            <v-btn :icon="!showing ? 'mdi-menu-right' : 'mdi-menu-left'" @click="showing = !showing"/>
          </l-control>
          <l-tile-layer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            layer-type="base"
            name="OpenStreetMap"
          />
          <l-marker
            v-for="marker of all_markers" :key="`${marker.name}-${marker.kind}`"
            :lat-lng="[marker.location.lat, marker.location.lng]"
          >
            <l-icon class-name="" :options="{ iconSize: [50, 43], iconAnchor: [25, 43] }">
              <position-marker
                :color="maintenance_marker_color(marker)"
                :width="50" :height="43"
                :is_issue="marker.kind === MaintenanceKind.issue"
              />
            </l-icon>
            <l-tooltip>
              <p class="bold">{{ marker.name }}</p>
            </l-tooltip>
            <l-popup :options="{ closeOnClick: true, minWidth: '350', maxWidth: '900' }">
              <recap-card :marker="marker"/>
            </l-popup>
          </l-marker>
        </l-map>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
